import angular from "angular";
import {DateTime, TempusDominus} from '@eonasdan/tempus-dominus';
import moment from "moment/moment";
import set from "lodash/set";
import get from "lodash/get";

const display = {
    icons: {
        time: 'fa fa-clock-o',
        date: 'fa fa-calendar',
        up: 'fa fa-arrow-up',
        down: 'fa fa-arrow-down',
        previous: 'fa fa-chevron-left',
        next: 'fa fa-chevron-right',
        today: 'fa fa-calendar-check-o',
        clear: 'fa fa-trash',
        close: 'fa fa-check',
    },
    sideBySide: true,
    buttons: {
        today: true,
        clear: true,
        close: true,
    }
};

const dateMatchRegex = new RegExp('\\b\\d{4}-\\d{2}-\\d{2}\\s\\d{2}:\\d{2}\\s(AM|PM|UTC)\\b');

// date-time-picker
const dateTimePickerDirective = ($rootScope) => {
    return {
        require: '?ngModel',
        restrict: 'AE',
        scope: {
            ngModel: '=',
            useCurrent: '@',
            showTime: '@',
            showSeconds: '@',
            noMax: '@',
            displayInUtc: '@',
            modelIsDate: '@',
            allowBlank: '@',
        },
        link: function (scope, elem, attrs, ngModel) {
            if (!ngModel) {
                return;
            }

            // wrap element in a parent div if the parent is a td
            if (elem.parent()[0].tagName.toLowerCase() === 'td') {
                const wrapper = angular.element('<div></div>');
                elem.wrap(wrapper);
            }

            if (!attrs.id) {
                attrs.id = 'datetimepicker-' + Math.floor(Math.random() * 1000000000);
            }

            const noMax = scope.noMax === 'true' || scope.noMax === '';
            const modelIsDate = scope.modelIsDate === 'true' || scope.modelIsDate === '';
            const allowBlank = scope.allowBlank === 'true' || scope.allowBlank === '';
            if (!modelIsDate && scope.ngModel && scope.ngModel.includes && scope.ngModel.includes('T')) {
                scope.ngModel = scope.ngModel.replace('T', ' ');
            }

            const displayInUtc =
                scope.displayInUtc === undefined && $rootScope.isOrs
                    ? true
                    : scope.displayInUtc === 'true';

            const jqElem = $(elem);

            jqElem.css('position', 'relative');

            let format = scope.showSeconds ? 'yyyy-MM-dd hh:mm:ss' : 'yyyy-MM-dd hh:mm';

            const momentFormat = format.replace('yyyy-MM-dd hh', 'YYYY-MM-DD HH');
            let momentFormatDisplay = momentFormat;

            if (displayInUtc) {
                momentFormatDisplay = momentFormat.replace('hh', 'HH');
                momentFormatDisplay += ' UTC';
            } else {
                momentFormatDisplay = momentFormat.replace('HH', 'hh');
                momentFormatDisplay += ' A';
            }

            if (displayInUtc) {
                format = format.replace('hh', 'HH');
                format += ` U[T]C`;
            } else {
                format += ' T';
            }

            const localization = {
                format,
                hourCycle: displayInUtc ? 'h23' : 'h12',
            }

            const localAsUtc = (date) => {
                date = date || new Date();
                return new Date(moment(date).utc().format().replace('T', ' ').replace('Z', ''))
            }

            const restrictions = noMax
                ? {} : {
                    maxDate: displayInUtc ? localAsUtc() : moment().toDate()
                };

            // convert a date like '2025-10-24 10:17 PM' to '2025-10-24T22:17:00'
            // this fixed new Date() parsing on Safari and iOS
            const toLocalISO = (dateStr) => {
                if (typeof dateStr !== 'string') {
                    return dateStr;
                }

                const trimmed = dateStr.trim();

                // YYYY-MM-DD HH:MM AM/PM/UTC (strict structure; AM/PM case-insensitive)
                const regEx = /^(\d{4})-(\d{2})-(\d{2})\s+(\d{1,2}):([0-5]\d)\s*(AM|PM|UTC)$/i;
                const match = trimmed.match(regEx);
                if (!match) {
                    // not the expected format
                    return dateStr;
                }

                let [, y, mo, d, h12, mi, mer] = match;
                const year = +y;
                const month = +mo; // 1–12
                const day = +d; // 1–31
                let hour = +h12; // 1–12

                if (month < 1 || month > 12) {
                    return dateStr;
                }

                mer = mer.toUpperCase();
                if (mer === 'UTC') {
                    if (hour < 0 || hour > 23) {
                        return dateStr;
                    }
                } else {
                    if (hour < 1 || hour > 12) {
                        return dateStr;
                    }
                }

                // Validate the actual calendar date (e.g., 2025-02-30 should fail)
                const probe = new Date(year, month - 1, day);
                if (
                    probe.getFullYear() !== year ||
                    probe.getMonth() + 1 !== month ||
                    probe.getDate() !== day
                ) {
                    return dateStr;
                }

                // Convert 12h -> 24h
                if (mer === 'AM' && hour === 12) {
                    hour = 0;
                }
                if (mer === 'PM' && hour < 12) {
                    hour += 12;
                }

                const pad = n => String(n).padStart(2, '0');
                const suffix = mer === 'UTC' ? 'Z' : '';

                // Build ISO-like local string (no timezone suffix)
                return `${year}-${pad(month)}-${pad(day)}T${pad(hour)}:${pad(+mi)}:00${suffix}`;
            }

            const getFormattedDatesFromModel = () => {
                if (allowBlank && !scope.ngModel) {
                    return { cleaned: '', defaultDate: undefined };
                }
                const defaultDateMoment = scope.ngModel ? moment(new Date(toLocalISO(scope.ngModel))) : moment();
                const base = displayInUtc ? defaultDateMoment.utc() : defaultDateMoment.local();
                const cleaned = base.format().replace('T', ' ').replace('Z', '');
                const defaultDate = new Date(cleaned);
                return { cleaned, defaultDate };
            }

            const { cleaned, defaultDate } = getFormattedDatesFromModel();

            const getContainer = () => {
                const containerId = `${attrs.id}-widget-container`;
                const container = document.getElementById(containerId);
                if (container) {
                    return container;
                }
                // Build the element with DOM APIs and set the id via property so attrs.id
                // is never parsed as HTML (prevents XSS).
                const newContainer = document.createElement('div');
                newContainer.id = containerId;
                jqElem.parent().after(newContainer);
                return newContainer;
            }

            const picker = new TempusDominus(elem[0], {container: getContainer()});
            picker.clear();

            const options = {
                useCurrent: !!scope.useCurrent,
                keepInvalid: true,
                display,
                localization,
                restrictions
            }

            if (defaultDate) {
                options.defaultDate = defaultDate;
            }

            picker.updateOptions(options);

            // Fix: When displayInUtc is true, the "today" button should set UTC now, not local now
            if (displayInUtc) {
                const container = getContainer();
                container.addEventListener('click', (e) => {
                    if (e.target.closest('[data-action="today"]')) {
                        e.preventDefault();
                        e.stopPropagation();
                        const utcNow = localAsUtc();
                        picker.dates._dates[0] = new DateTime(utcNow);
                        picker.viewDate = new DateTime(utcNow);
                        elem[0].value = formatForDisplay(moment(utcNow).format());
                        setValue(utcNow);
                    }
                }, true);
            }

            picker.subscribe('update.td', (e) => {
                // this fires when changing the month view (and when opening the popup UI for the first time)
                // we don't want to change the selected date automatically on a month view change... this fixes that
                if (ngModel.$viewValue) {
                    const localOrUtc = displayInUtc ? localAsUtc(ngModel.$viewValue) : ngModel.$viewValue;
                    picker.dates._dates[0] = new DateTime(localOrUtc);
                }
            });

            const utcFromDate = (date) => {
                const m = moment(date);
                return m.format('YYYY-MM-DDTHH:mm:ss') + 'Z';
            }

            const makeDateInUtc = (date) => {
                return new Date(utcFromDate(date));
            }

            const setValue = (dt = undefined) => {
                const noPickerDateSelected = picker.dates._dates.length === 0;
                if (!dt && noPickerDateSelected && allowBlank) {
                    ngModel.$setViewValue('');
                    return;
                }

                const vd = dt || picker.viewDate;
                const value = displayInUtc ? makeDateInUtc(vd) : vd;

                if (modelIsDate) {
                    ngModel.$setViewValue(value);
                } else {
                    const formatted = moment(value).format(momentFormat);
                    ngModel.$setViewValue(formatted);
                }
            }

            const formatForDisplay = (str) =>
                str || !allowBlank
                    ? moment(str).format(momentFormatDisplay)
                    : '';

            elem[0].value = formatForDisplay(cleaned);

            if (defaultDate) {
                setValue(defaultDate);
            } else {
                ngModel.$setViewValue('');
            }

            jqElem.on('change', function () {
                setValue();
            });

            scope.$watch('ngModel', function () {

                if (allowBlank && !scope.ngModel) {
                    elem[0].value = '';
                    return;
                }

                const goingToBlank = allowBlank && !scope.ngModel;

                if (!goingToBlank && (scope.ngModel || scope.ngModel === '') && scope.ngModel.replace) {
                    if (!dateMatchRegex.test(scope.ngModel)) {
                        return;
                    }
                }

                const { cleaned, defaultDate } = getFormattedDatesFromModel();

                picker.updateOptions({
                    defaultDate: defaultDate,
                    viewDate: defaultDate
                });

                // DateTime is a class from the tempus-dominus library
                const pickerDate = new DateTime(defaultDate);

                picker.viewDate = pickerDate;

                // have to hack this private property to get the picker to update if there is already a value
                const exists = get(picker, ['dates', '_dates', 0]);
                if (exists) {
                    set(picker, ['dates', '_dates', 0], pickerDate);
                }

                elem[0].value = formatForDisplay(cleaned);
            })
        }
    };
}

angular.module('ui-common').directive('dateTimePicker', ['$rootScope', dateTimePickerDirective]);
